import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Table, Input, Space, Tag, Tabs } from "antd";
import { useSnackbar } from "notistack";
import {
  SearchOutlined,
  CopyOutlined,
  CheckOutlined,
  MailOutlined,
  LinkOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import { getResellerUsers, getMyResellerCustomers } from "../../../utils/users";
import InviteCustomerModal from "../../../modals/InviteCustomerModal";

const getColumnSearchProps = (dataIndex, placeholder = "Search") => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={`${placeholder}...`}
        value={selectedKeys[0]}
        onChange={(e) =>
          setSelectedKeys(e.target.value ? [e.target.value] : [])
        }
        onPressEnter={() => confirm()}
        style={{ marginBottom: 8, display: "block" }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => confirm()}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button onClick={clearFilters} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
  ),
  onFilter: (value, record) =>
    record[dataIndex]
      ?.toString()
      .toLowerCase()
      .includes(value.toLowerCase()),
});

const ResellerUsers = () => {
  const navigate = useNavigate();
  const { resellerId } = useParams();
  const { isLoading, getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [resellerUsers, setResellerUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [referralCode, setReferralCode] = useState(null);
  const [resellerName, setResellerName] = useState(null);
  const [xeroDirectUrl, setXeroDirectUrl] = useState("");
  const [qboDirectUrl, setQboDirectUrl] = useState("");
  const [fetching, setFetching] = useState(false);
  const [xeroCopied, setXeroCopied] = useState(false);
  const [qboCopied, setQboCopied] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("organizations");

  useEffect(() => {
    if (!isLoading) {
      fetchResellerCustomers();
    }
  }, [isLoading, resellerId]);

  const fetchResellerCustomers = async () => {
    try {
      setFetching(true);
      setResellerUsers([]);
      setOrganizations([]);

      const token = await getIdTokenClaims();
      const apiCall = resellerId
        ? getResellerUsers(token.__raw, resellerId)
        : getMyResellerCustomers(token.__raw);

      apiCall
        .then((res) => {
          if (res.data?.success === false) {
            enqueueSnackbar(res.data?.message || "Failed to load customers", {
              variant: "error",
            });
          } else {
            setResellerUsers(res.data?.users || []);
            setOrganizations(res.data?.organizations || []);
            setReferralCode(res.data?.referral_code || null);
            setResellerName(res.data?.reseller_name || null);
            setXeroDirectUrl(res.data?.xero_direct_url || "");
            setQboDirectUrl(res.data?.qbo_direct_url || "");

            // If no organisations exist yet but users do, switch tab
            if (
              (!res.data?.organizations || res.data?.organizations.length === 0) &&
              res.data?.users &&
              res.data?.users.length > 0
            ) {
              setActiveTab("users");
            }
          }
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          setFetching(false);
        });
    } catch (e) {
      console.error(e);
      setFetching(false);
    }
  };

  const xeroInviteUrl = xeroDirectUrl;
  const qboInviteUrl = qboDirectUrl;

  const handleCopyLink = (url, type) => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    if (type === "xero") {
      setXeroCopied(true);
      setTimeout(() => setXeroCopied(false), 3000);
    } else {
      setQboCopied(true);
      setTimeout(() => setQboCopied(false), 3000);
    }
    enqueueSnackbar(
      `${type === "xero" ? "Xero" : "QuickBooks Online"} invite link copied!`,
      {
        variant: "success",
      }
    );
  };

  const orgColumns = [
    {
      title: "Organisation / Client Name",
      dataIndex: "name",
      key: "name",
      width: "35%",
      render: (_, record) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{record.name}</span>
          {record.email && (
            <span className="text-xs text-gray-400">{record.email}</span>
          )}
        </div>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      ...getColumnSearchProps("name", "Search organisation"),
    },
    {
      title: "Accounting Platform",
      dataIndex: "platform",
      key: "platform",
      render: (platform) => {
        const isXero = (platform || "").toLowerCase().includes("xero");
        return (
          <Tag color={isXero ? "blue" : "green"} className="font-medium px-2 py-0.5">
            {platform}
          </Tag>
        );
      },
      filters: [
        { text: "Xero", value: "Xero" },
        { text: "QuickBooks Online", value: "QuickBooks Online" },
      ],
      onFilter: (value, record) => record.platform === value,
    },
    {
      title: "Connected On",
      dataIndex: "created",
      key: "created",
      sorter: (a, b) => (a.created || "").localeCompare(b.created || ""),
      render: (val) => val || "—",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "Active" ? "success" : "default"}>
          {status}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={() => navigate("/organizations")}
          className="text-blue-600 p-0"
        >
          Manage Org
        </Button>
      ),
    },
  ];

  const userColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      render: (_, { name }) => (
        <div className={"flex gap-2 items-center text-gray-800 font-medium"}>
          {name}
        </div>
      ),
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      ...getColumnSearchProps("name", "Search name"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      ...getColumnSearchProps("email", "Search email"),
    },
    {
      title: "Subscribed Organisations",
      dataIndex: "subscribed_orgs_count",
      key: "subscribed_orgs_count",
      sorter: (a, b) =>
        (a.subscribed_orgs_count || 0) - (b.subscribed_orgs_count || 0),
    },
    {
      title: "Registration Date",
      dataIndex: "created",
      key: "created",
      sorter: (a, b) => (a.created || "").localeCompare(b.created || ""),
    },
    {
      title: "Referral Redemption Date",
      dataIndex: "used_at",
      key: "used_at",
      sorter: (a, b) => (a.used_at || "").localeCompare(b.used_at || ""),
    },
  ];

  const tabItems = [
    {
      key: "organizations",
      label: `Connected Organisations (${organizations.length})`,
      children: (
        <Table
          dataSource={organizations}
          columns={orgColumns}
          rowKey={(rec) => rec.id || rec.org_id}
          loading={fetching}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 20,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} organisations`,
          }}
        />
      ),
    },
    {
      key: "users",
      label: `Referral Accounts (${resellerUsers.length})`,
      children: (
        <Table
          dataSource={resellerUsers}
          columns={userColumns}
          rowKey={(rec, index) => rec.email || index}
          loading={fetching}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            defaultPageSize: 20,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} users`,
          }}
        />
      ),
    },
  ];

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          {resellerId && (
            <Button
              type="default"
              onClick={() => navigate("/resellers")}
              className="mb-3 text-xs"
            >
              ← Back to Resellers
            </Button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {resellerId
              ? `${resellerName || "Reseller"}'s Customers`
              : "My Customers"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {resellerId
              ? "All client accounts and organisations connected under this reseller profile."
              : "Manage client organisations connected via your partner link and invite new clients."}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">

          <Button
            type="primary"
            icon={<MailOutlined />}
            onClick={() => setInviteModalOpen(true)}
            disabled={!referralCode}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Invite Customer
          </Button>
        </div>
      </div>

      {/* Invite Link Banner Card */}
      {referralCode && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-blue-900 font-semibold text-sm">
                <LinkOutlined className="text-blue-600" />
                <span>Your Partner Invitation Links</span>
                <span className="text-xs bg-blue-100 text-blue-800 font-mono px-2 py-0.5 rounded">
                  Code: {referralCode}
                </span>
              </div>
              <p className="text-xs text-blue-700 max-w-2xl leading-relaxed">
                Send these links or use the <strong>Invite Customer</strong> button
                to invite clients. They can connect their Xero or QuickBooks
                Online account directly in one click, and their organisation will
                be managed under your reseller dashboard.
              </p>
            </div>

            <div className="flex flex-col gap-2 min-w-[320px]">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-900 w-10">
                  Xero
                </span>
                <Input
                  value={xeroInviteUrl}
                  readOnly
                  className="bg-white font-mono text-xs text-gray-700 select-all"
                />
                <Button
                  type="primary"
                  icon={xeroCopied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => handleCopyLink(xeroInviteUrl, "xero")}
                  className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                >
                  {xeroCopied ? "Copied" : "Copy"}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-900 w-24">
                  QuickBooks
                </span>
                <Input
                  value={qboInviteUrl}
                  readOnly
                  className="bg-white font-mono text-xs text-gray-700 select-all"
                />
                <Button
                  type="primary"
                  icon={qboCopied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={() => handleCopyLink(qboInviteUrl, "qbo")}
                  className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                >
                  {qboCopied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grid Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          items={tabItems}
        />
      </div>

      {/* Invite Customer Modal */}
      <InviteCustomerModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        referralCode={referralCode}
        resellerId={resellerId}
        xeroDirectUrl={xeroDirectUrl}
        qboDirectUrl={qboDirectUrl}
      />
    </div>
  );
};

export default ResellerUsers;
